import { useState } from 'react';
import { FileText, Image, Download, Eye, MoreVertical, Trash2, Link2 } from 'lucide-react';
import { documentsApi } from '@/api/endpoints/documents.api';
import type { DocumentListItem } from '@/types/document.types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface DocumentCardProps {
  document: DocumentListItem;
  onDelete?: () => void;
  onLinkToTask?: () => void;
  onLinkToDemand?: () => void;
  onViewDetails?: () => void;
  showActions?: boolean;
}

const DocumentTypeColors: Record<string, string> = {
  LAB_RESULT: 'bg-blue-100 text-blue-700',
  PRESCRIPTION: 'bg-purple-100 text-purple-700',
  MEDICAL_REPORT: 'bg-green-100 text-green-700',
  IMAGING_REPORT: 'bg-cyan-100 text-cyan-700',
  DISCHARGE_SUMMARY: 'bg-orange-100 text-orange-700',
  REFERRAL: 'bg-yellow-100 text-yellow-700',
  CERTIFICATE: 'bg-pink-100 text-pink-700',
  CONSENT: 'bg-gray-100 text-gray-700',
  OTHER: 'bg-slate-100 text-slate-700',
};

export default function DocumentCard({
  document,
  onDelete,
  onLinkToTask,
  onLinkToDemand,
  onViewDetails,
  showActions = true,
}: DocumentCardProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [viewing, setViewing] = useState(false);

  const isImage = document.mimeType?.startsWith('image/');
  const isPdf = document.mimeType === 'application/pdf';

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const blob = await documentsApi.download(document.id);
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = document.originalFileName;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erro ao baixar documento:', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleView = async () => {
    setViewing(true);
    try {
      const blob = await documentsApi.download(document.id);
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Revoke the URL after a delay to allow the new tab to load
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 60000);
    } catch (err) {
      console.error('Erro ao visualizar documento:', err);
    } finally {
      setViewing(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'dd/MM/yyyy', { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  return (
    <div
      className={`group relative rounded-lg border bg-card p-4 transition-shadow hover:shadow-md ${
        onViewDetails ? 'cursor-pointer' : ''
      }`}
      onClick={(e) => {
        // Only trigger if not clicking on a button or menu
        if (onViewDetails && (e.target as HTMLElement).closest('button, [role="menu"]') === null) {
          onViewDetails();
        }
      }}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="flex-shrink-0">
          {isImage ? (
            <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded bg-muted">
              <Image className="h-6 w-6 text-muted-foreground" />
            </div>
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded bg-muted">
              <FileText className="h-6 w-6 text-muted-foreground" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="min-w-0">
              <p className="truncate font-medium" title={document.title || document.originalFileName}>
                {document.title || document.originalFileName}
              </p>
              <p className="text-sm text-muted-foreground">
                {document.fileSizeFormatted} • {formatDate(document.documentDate)}
              </p>
            </div>

            {/* Actions Menu */}
            {showActions && (
              <div className="relative">
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <MoreVertical className="h-4 w-4" />
                </button>

                {showMenu && (
                  <>
                    <div className="fixed inset-0" onClick={() => setShowMenu(false)} />
                    <div className="absolute right-0 z-10 mt-1 w-48 rounded-lg border bg-background py-1 shadow-lg">
                      <button
                        onClick={() => {
                          handleView();
                          setShowMenu(false);
                        }}
                        disabled={viewing}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted disabled:opacity-50"
                      >
                        <Eye className="h-4 w-4" />
                        {viewing ? 'Carregando...' : 'Visualizar'}
                      </button>
                      <button
                        onClick={() => {
                          handleDownload();
                          setShowMenu(false);
                        }}
                        disabled={downloading}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted disabled:opacity-50"
                      >
                        <Download className="h-4 w-4" />
                        Baixar
                      </button>
                      {onLinkToTask && (
                        <button
                          onClick={() => {
                            onLinkToTask();
                            setShowMenu(false);
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                        >
                          <Link2 className="h-4 w-4" />
                          Vincular a Tarefa
                        </button>
                      )}
                      {onLinkToDemand && (
                        <button
                          onClick={() => {
                            onLinkToDemand();
                            setShowMenu(false);
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                        >
                          <Link2 className="h-4 w-4" />
                          Vincular a Demanda
                        </button>
                      )}
                      {onDelete && (
                        <>
                          <hr className="my-1" />
                          <button
                            onClick={() => {
                              onDelete();
                              setShowMenu(false);
                            }}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                            Excluir
                          </button>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Tags */}
          <div className="mt-2 flex flex-wrap gap-2">
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${DocumentTypeColors[document.documentType] || DocumentTypeColors.OTHER}`}>
              {document.documentTypeLabel}
            </span>
            {document.uploadedByPatient && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                Enviado pelo paciente
              </span>
            )}
            {document.taskId && (
              <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700">
                Vinculado a tarefa
              </span>
            )}
            {document.demandId && (
              <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-700">
                Vinculado a demanda
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions (visible on hover) */}
      <div className="absolute bottom-2 right-2 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
        {(isImage || isPdf) && (
          <button
            onClick={handleView}
            disabled={viewing}
            className="rounded bg-primary/10 p-1.5 text-primary hover:bg-primary/20 disabled:opacity-50"
            title="Visualizar"
          >
            <Eye className="h-4 w-4" />
          </button>
        )}
        <button
          onClick={handleDownload}
          disabled={downloading}
          className="rounded bg-primary/10 p-1.5 text-primary hover:bg-primary/20 disabled:opacity-50"
          title="Baixar"
        >
          <Download className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
